import * as React from 'react';
import SearchField from '../../components/SearchField';
import { useEffect, useState } from 'react';
import useProduction from '@/src/Production/components/hooks/useProduction';

/* Gear inventory — category-grouped read-only list. */

const GEAR_STATUS_CLASSES: Record<string, string> = {
    available: 'bg-good-soft text-good',
    in_use: 'bg-brand-soft text-brand-ink',
    retired: 'bg-sunk text-faint',
    needs_repair: 'border border-attention/40 text-attention',
};

const CATEGORIES = [
    'photobooth', 'printer', 'lighting', 'computer', 'phone',
    'tripods_stabilizers_rigs', 'audio', 'router', 'camera', 'lens', 'tablet',
];
const STUDIOS = [
    'nyc', 'nashville', 'atlanta', 'houston', 'austin', 'los_angeles',
    'seattle', 'spokane', 'chicago',
];

export default function GearV2() {
    const { getGear, createGearAsset, updateGearAsset, deleteGearAsset } =
        useProduction();
    const [rows, setRows] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const [addFields, setAddFields] = useState({
        name: '',
        category: 'photobooth',
        studioLocation: 'nyc',
        serialNumber: '',
    });
    const [notice, setNotice] = useState<string | null>(null);

    const reload = async () => {
        setRows((await getGear().catch(() => [])) ?? []);
    };

    const submitAdd = async () => {
        if (!addFields.name.trim()) return;
        const ok = await createGearAsset({
            name: addFields.name.trim(),
            category: addFields.category,
            studioLocation: addFields.studioLocation,
            serialNumber: addFields.serialNumber.trim() || null,
        })
            .then(() => true)
            .catch(() => false);
        setNotice(ok ? null : 'Add failed (duplicate serial?)');
        if (ok) {
            setAddFields((f) => ({ ...f, name: '', serialNumber: '' }));
            setAddOpen(false);
            await reload();
        }
    };

    useEffect(() => {
        let mounted = true;
        (async () => {
            const data = await getGear().catch(() => []);
            if (!mounted) return;
            setRows(data ?? []);
            setLoading(false);
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const q = search.trim().toLowerCase();
    const filtered = rows.filter(
        (r) =>
            !q ||
            (r.name ?? '').toLowerCase().includes(q) ||
            (r.serialNumber ?? '').toLowerCase().includes(q) ||
            (r.brand ?? '').toLowerCase().includes(q),
    );

    const categories = Array.from(new Set(filtered.map((r) => r.category)));

    return (
        <main className="mx-auto max-w-[1060px] px-[clamp(18px,3vw,34px)] pt-4">
            <div className="ps-label mb-3.5">Jobs · Gear</div>

            <div className="mb-4 flex items-center gap-3">
                <span className="text-[13px] text-muted-foreground">
                    <b className="font-semibold text-foreground">
                        {filtered.length}
                    </b>{' '}
                    assets
                </span>
                {notice && (
                    <span className="text-[12px] font-semibold text-attention">
                        {notice}
                    </span>
                )}
                <div className="flex-1" />
                <SearchField
                    value={search}
                    onChange={setSearch}
                    placeholder="Search gear…"
                    className="w-full max-w-[260px]"
                />
                <button
                    type="button"
                    onClick={() => setAddOpen((o) => !o)}
                    className="cursor-pointer rounded-full border-0 bg-brand px-4 py-2 font-[inherit] text-[12.5px] font-semibold text-brand-foreground"
                >
                    Add gear
                </button>
            </div>

            {addOpen && (
                <div className="mb-4 flex flex-wrap items-center gap-2.5 rounded-2xl border border-dashed border-border p-3">
                    <input
                        autoFocus
                        className="ps-input w-[220px] text-[12.5px]"
                        placeholder="Asset name"
                        value={addFields.name}
                        onChange={(e) =>
                            setAddFields((f) => ({ ...f, name: e.target.value }))
                        }
                    />
                    <select
                        className="ps-input cursor-pointer appearance-none text-[12.5px]"
                        value={addFields.category}
                        onChange={(e) =>
                            setAddFields((f) => ({
                                ...f,
                                category: e.target.value,
                            }))
                        }
                    >
                        {CATEGORIES.map((c) => (
                            <option key={c} value={c}>
                                {c.replaceAll('_', ' ')}
                            </option>
                        ))}
                    </select>
                    <select
                        className="ps-input cursor-pointer appearance-none text-[12.5px]"
                        value={addFields.studioLocation}
                        onChange={(e) =>
                            setAddFields((f) => ({
                                ...f,
                                studioLocation: e.target.value,
                            }))
                        }
                    >
                        {STUDIOS.map((s) => (
                            <option key={s} value={s}>
                                {s.replaceAll('_', ' ')}
                            </option>
                        ))}
                    </select>
                    <input
                        className="ps-input w-[150px] font-mono text-[12px]"
                        placeholder="Serial #"
                        value={addFields.serialNumber}
                        onChange={(e) =>
                            setAddFields((f) => ({
                                ...f,
                                serialNumber: e.target.value,
                            }))
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') submitAdd();
                        }}
                    />
                    <button
                        type="button"
                        disabled={!addFields.name.trim()}
                        onClick={submitAdd}
                        className="cursor-pointer rounded-full border-0 bg-brand px-4 py-1.5 font-[inherit] text-[12px] font-semibold text-brand-foreground disabled:opacity-50"
                    >
                        Add
                    </button>
                </div>
            )}

            {loading && (
                <div>
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className="mb-4 h-16 animate-pulse rounded-[12px] bg-secondary"
                        />
                    ))}
                </div>
            )}
            {!loading && filtered.length === 0 && (
                <div className="py-10 text-[13px] text-faint">
                    No gear found.
                </div>
            )}
            {categories.map((cat) => (
                <section key={cat} className="mb-1">
                    <div className="ps-label mb-1.5 mt-6 first:mt-0">
                        {String(cat).replaceAll('_', ' ')}
                    </div>
                    {filtered
                        .filter((r) => r.category === cat)
                        .map((r) => (
                            <div
                                key={r.id}
                                className="flex items-center gap-3.5 border-t border-line px-0.5 py-[10px] text-[12.5px] first:border-t-0"
                            >
                                <span className="min-w-0 flex-1 basis-[140px] truncate font-semibold sm:flex-none sm:basis-auto sm:w-[220px]">
                                    {r.name}
                                </span>
                                <span className="hidden w-[160px] flex-none truncate text-muted-foreground sm:block">
                                    {[r.brand, r.model]
                                        .filter(Boolean)
                                        .join(' ') || '—'}
                                </span>
                                <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-faint">
                                    {r.serialNumber ?? '—'}
                                </span>
                                <span className="hidden w-[110px] flex-none text-muted-foreground md:block">
                                    {String(r.studioLocation).replaceAll(
                                        '_',
                                        ' ',
                                    )}
                                </span>
                                <select
                                    className={
                                        'h-[22px] flex-none cursor-pointer appearance-none rounded-full border-0 px-2.5 font-[inherit] text-[10.5px] font-semibold ' +
                                        (GEAR_STATUS_CLASSES[r.status] ??
                                            'bg-sunk text-muted-foreground')
                                    }
                                    value={r.status}
                                    onChange={async (e) => {
                                        await updateGearAsset(+r.id, {
                                            status: e.target.value,
                                        }).catch(() => null);
                                        await reload();
                                    }}
                                >
                                    {['available', 'in_use', 'retired', 'needs_repair'].map((s) => (
                                        <option key={s} value={s}>
                                            {s.replaceAll('_', ' ')}
                                        </option>
                                    ))}
                                </select>
                                <button
                                    type="button"
                                    title="Delete gear asset"
                                    aria-label={`Delete ${r.name}`}
                                    onClick={async () => {
                                        if (
                                            !window.confirm(
                                                `Delete "${r.name}"? This can't be undone.`,
                                            )
                                        )
                                            return;
                                        const ok = await deleteGearAsset(+r.id)
                                            .then(() => true)
                                            .catch(() => false);
                                        setNotice(
                                            ok
                                                ? null
                                                : 'Cannot delete — has assignment history',
                                        );
                                        await reload();
                                    }}
                                    className="cursor-pointer border-0 bg-transparent p-0 text-faint hover:text-attention"
                                >
                                    ✕
                                </button>
                            </div>
                        ))}
                </section>
            ))}
        </main>
    );
}
